import type { StateSetter } from "@/base/types"
import type { AcceptsChildren, ButtonProps, IconProp, TestId } from "@/theme"
import type { FormControlProviderProps } from "@/theme/form-control/FormControlProvider"
import type { InputProps } from "@/theme/form-control/Input"
import type { FormControlLabelProps } from "@/theme/form-control/Label"

import { Link, useRouter } from "expo-router"
import React from "react"
import { Controller } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { isTruthyString } from "@/base/type-guards"
import { SuccessHandler } from "@/components/SuccessHandler"
import { useFormValidation } from "@/hooks/useFormvalidation"
import {
  getIsUserAuthenticatedFromState,
  signOutAsync,
  useAuthFormContext,
  useValidatePassword,
} from "@/redux/auth.reducer"
import { useIsPending } from "@/redux/requests.reducer"
import { useRootDispatch } from "@/redux/useRootDispatch"
import { useRootSelector } from "@/redux/useRootSelector"
import {
  AppText,
  Box,
  Button,
  FormControl,
  Pressable,
  Row,
  Theme,
} from "@/theme"
import { AppIcons } from "@/theme/icons/AppIcons"
import { renderIconFromIconProp } from "@/theme/icons/renderIconFromIconProp"
import { ICON_SIZES } from "@/theme/theme/icon-sizes"

import { StyleSheet } from "react-native"

interface AuthFieldProps extends FormControlProviderProps {
  _input?: InputProps
}

function usePasswordFieldVisibility() {
  const [isVisible, setIsVisible] = React.useState(false)

  const visibilityProps: InputProps = {}
  if (isVisible) {
    visibilityProps.textContentType = "none"
    visibilityProps.secureTextEntry = false
  }

  return { isVisible, setIsVisible, visibilityProps }
}

function PasswordVisibilityButton({
  isVisible,
  setIsVisible,
}: {
  isVisible: boolean
  setIsVisible: StateSetter<boolean>
}) {
  const IconComponent = isVisible
    ? AppIcons.VisibilityOff
    : AppIcons.VisibilityOn
  return (
    <Pressable onPress={() => setIsVisible((prev) => !prev)}>
      <Box mr={"$3"}>
        {renderIconFromIconProp(IconComponent, { size: ICON_SIZES.$sm })}
      </Box>
    </Pressable>
  )
}

function FieldLabel({ children, ...rest }: FormControlLabelProps) {
  if (isTruthyString(children)) {
    return <FormControl.Label {...rest}>{children}</FormControl.Label>
  }
  return null
}

export function EmailField({
  _input,
  // label,
  ...formControlProps
}: AuthFieldProps): React.JSX.Element {
  const form = useAuthFormContext()
  const { required } = useFormValidation()
  const { t } = useTranslation("auth")
  const labelText =
    //  label ??
    t("emailFieldLabel")
  const placeholderText = t("emailFieldPlaceholder")
  return (
    <Controller
      control={form.control}
      name={"email"}
      rules={{ required }}
      render={({ field: { onChange, ...field }, fieldState }) => {
        const errorMessage = fieldState.error?.message
        const isInvalid = Boolean(errorMessage)
        return (
          <FormControl.Provider
            id={"email"}
            isInvalid={isInvalid}
            {...formControlProps}
          >
            <FieldLabel>{labelText}</FieldLabel>
            <FormControl.Input
              {..._input}
              autoCapitalize={"none"}
              autoComplete={"username"}
              importantForAutofill={"yes"}
              keyboardType={"email-address"}
              placeholder={placeholderText}
              textContentType={"emailAddress"}
              onChangeText={onChange}
              {...field}
            />
            <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
          </FormControl.Provider>
        )
      }}
    />
  )
}

/**
 * CurrentPasswordField
 */
export function CurrentPasswordField({
  _input,
  ...formControlProps
}: AuthFieldProps): React.JSX.Element {
  const { control } = useAuthFormContext()
  const { required } = useFormValidation()

  const { isVisible, setIsVisible, visibilityProps } =
    usePasswordFieldVisibility()
  const { t } = useTranslation("auth")
  const labelText =
    //  label ??
    t("currentPasswordFieldLabel")
  const placeholderText = t("currentPasswordFieldPlaceholder")
  return (
    <Controller
      control={control}
      name={"password"}
      rules={{ required }}
      render={({ field: { onChange, ...field }, fieldState }) => {
        const errorMessage = fieldState.error?.message
        const isInvalid = Boolean(errorMessage)

        return (
          <FormControl.Provider
            {...formControlProps}
            id={"password"}
            isInvalid={isInvalid}
          >
            <FieldLabel>{labelText}</FieldLabel>
            <FormControl.Input
              {..._input}
              autoCapitalize={"none"}
              autoComplete={"password"}
              importantForAutofill={"yes"}
              placeholder={placeholderText}
              textContentType={"password"}
              InputRightElement={
                <PasswordVisibilityButton
                  isVisible={isVisible}
                  setIsVisible={setIsVisible}
                />
              }
              onChangeText={onChange}
              {...field}
              {...visibilityProps}
            />
            <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
          </FormControl.Provider>
        )
      }}
    />
  )
}

export function NewPasswordField({
  _input,
  ...props
}: AuthFieldProps): React.JSX.Element {
  const form = useAuthFormContext()
  const { isVisible, setIsVisible, visibilityProps } =
    usePasswordFieldVisibility()
  const { t } = useTranslation("auth")
  const validatePassword = useValidatePassword()
  const { required } = useFormValidation()
  const labelText =
    // label ??
    t("newPasswordFieldLabel")
  const placeholderText = t("newPasswordFieldPlaceholder")
  return (
    <Controller
      control={form.control}
      name={"newPassword"}
      render={({ field: { onChange, ref, ...field }, fieldState }) => {
        const errorMessage = fieldState.error?.message
        const isInvalid = Boolean(errorMessage)
        return (
          <FormControl.Provider
            {...props}
            id={"newPassword"}
            isInvalid={isInvalid}
          >
            <FieldLabel>{labelText}</FieldLabel>
            <FormControl.Input
              ref={ref}
              {..._input}
              autoCapitalize={"none"}
              autoComplete={"password-new"}
              autoCorrect={false}
              keyboardType={"default"}
              placeholder={placeholderText}
              textContentType={"newPassword"}
              InputRightElement={
                <PasswordVisibilityButton
                  isVisible={isVisible}
                  setIsVisible={setIsVisible}
                />
              }
              onChangeText={onChange}
              {...visibilityProps}
              {...field}
            />
            <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
          </FormControl.Provider>
        )
      }}
      rules={{
        required,
        validate: validatePassword,
      }}
    />
  )
}
export function ConfirmPasswordField({
  _input,
  ...formControlProps
}: AuthFieldProps): React.JSX.Element {
  const { t } = useTranslation("auth")
  const { required } = useFormValidation()
  const form = useAuthFormContext()
  const { isVisible, setIsVisible, visibilityProps } =
    usePasswordFieldVisibility()
  const labelText = t("confirmPasswordFieldLabel")
  const placeholderText = t("confirmPasswordFieldPlaceholder")
  return (
    <Controller
      control={form.control}
      name={"confirmPassword"}
      render={({ field: { onChange, ref, ...field }, fieldState }) => {
        const errorMessage = fieldState.error?.message
        const isInvalid = Boolean(errorMessage)
        return (
          <FormControl.Provider
            {...formControlProps}
            id={"confirmPassword"}
            isInvalid={isInvalid}
          >
            <FieldLabel>{labelText}</FieldLabel>
            <FormControl.Input
              ref={ref}
              {..._input}
              autoCapitalize={"none"}
              placeholder={placeholderText}
              textContentType={"password"}
              InputRightElement={
                <PasswordVisibilityButton
                  isVisible={isVisible}
                  setIsVisible={setIsVisible}
                />
              }
              onChangeText={onChange}
              {...visibilityProps}
              {...field}
            />
            <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
          </FormControl.Provider>
        )
      }}
      rules={{
        required,
        validate: (value) => {
          if (value !== form.watch("newPassword")) {
            return t("passwordRulesMustMatch", { ns: "validation" })
          }
          return undefined
        },
      }}
    />
  )
}

export function CodeStringField({
  _button,
  _input,
  labelText,
  ...rest
}: AuthFieldProps & {
  _button?: ButtonProps
  labelText?: string
}): React.JSX.Element {
  const { t } = useTranslation("auth")
  const { required } = useFormValidation()
  const form = useAuthFormContext()
  if (typeof labelText === "undefined") {
    labelText = t("codeStringFieldLabel")
  }
  const placeholderText = t("codeStringFieldPlaceholder")
  return (
    <Controller
      control={form.control}
      name={"codeString"}
      rules={{ required }}
      render={({ field: { onChange, ...field }, fieldState }) => {
        const errorMessage = fieldState.error?.message
        const isInvalid = Boolean(errorMessage)
        return (
          <FormControl.Provider
            {...rest}
            id={"codeString"}
            isInvalid={isInvalid}
          >
            <FieldLabel>{labelText}</FieldLabel>
            <Row justifyContent={"space-between"}>
              <FormControl.Input
                autoCapitalize={"characters"}
                autoComplete={"sms-otp"}
                autoCorrect={false}
                keyboardType={"number-pad"}
                placeholder={placeholderText}
                style={styles.codeStringInput}
                textContentType={"oneTimeCode"}
                onChangeText={(nextValue) =>
                  onChange(nextValue.toLocaleUpperCase())
                }
                {...field}
                {..._input}
              />
              {_button?.onPress ? (
                <Button
                  IconComponent={"Refresh"}
                  id={"resendCodeBtn"}
                  size={"md"}
                  variant={"text"}
                  {..._button}
                  text={t("resendCode", { ns: "common" })}
                />
              ) : null}
            </Row>
            <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
          </FormControl.Provider>
        )
      }}
    />
  )
}

export function AuthSuccess({
  children,
}: AcceptsChildren): React.JSX.Element | null {
  const { t } = useTranslation("auth")

  return (
    <SuccessHandler message={t("credentialsSuccess")}>
      <Box my={"$4"}>
        <Link asChild href={"/sign-in"}>
          <AppText id={"signInLink"}>{t("signIn")}</AppText>
        </Link>
      </Box>

      {children}
    </SuccessHandler>
  )
}

export function useSignOutLink(props?: { onSuccess?: () => void }) {
  const { t } = useTranslation()
  const dispatch = useRootDispatch()
  const router = useRouter()
  const IconComponent: IconProp = "SignOut"
  const id: TestId = "signOutLink"

  return {
    IconComponent,
    id,
    isDisabled: useIsPending("signOut"),
    onPress: () => {
      return dispatch(signOutAsync())
        .unwrap()
        .then(props?.onSuccess)
        .then(() => router.navigate("/"))
    },
    text: t("signOut"),
  }
}
/**
 * Clear auth status
 */
export function SignOutButton({
  onSuccess,
  ...props
}: Pick<ButtonProps, "isDisabled" | "size" | "style" | "text" | "variant"> & {
  onSuccess?: () => void
}): React.JSX.Element | null {
  const isAuthenticated = useRootSelector(getIsUserAuthenticatedFromState)
  const { id, onPress, text } = useSignOutLink({ onSuccess })
  if (isAuthenticated) {
    return (
      <Button
        id={id}
        text={text}
        variant={"text"}
        onPress={onPress}
        {...props}
      />
    )
  }
  return null
}
const styles = StyleSheet.create({
  codeStringInput: {
    flexGrow: 1,
    marginBottom: Theme.SPACING.$2,
    minWidth: 180,
  },
})
